/**
 * Hook to track online/offline connectivity status.
 * Uses navigator.onLine + periodic ping for reliability.
 */

'use client';

import { useState, useEffect, useCallback } from 'react';

export type ConnectionStatus = 'online' | 'offline' | 'syncing';

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [status, setStatus] = useState<ConnectionStatus>('online');
  const [pendingChanges, setPendingChanges] = useState(0);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setStatus('online');
      console.log('[Network] Back online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      setStatus('offline');
      console.log('[Network] Gone offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    setIsOnline(navigator.onLine);
    setStatus(navigator.onLine ? 'online' : 'offline');

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const setSyncing = useCallback((syncing: boolean) => {
    if (syncing) {
      setStatus('syncing');
    } else {
      setStatus(isOnline ? 'online' : 'offline');
    }
  }, [isOnline]);

  return {
    isOnline,
    status,
    pendingChanges,
    setPendingChanges,
    setSyncing,
  };
}
